import { z } from "zod";
import axiosInstance from "./axios";
import { validateWithSchema } from "./zodUtils";
import {
  extractArrayBufferErrorJson,
  extractArrayBufferErrorMessage,
  extractHeaderStringArray,
} from "./apiErrorUtils";

const apiUrl = import.meta.env.VITE_BACKEND_API_URL;

// Not documented in swagger, shape confirmed manually with backend
export const UploadProductsResponseSchema = z.object({
  createdProducts: z.number().optional(),
  errors: z.array(z.string()).optional(),
  warnings: z.array(z.string()).optional(),
});

export type UploadProductsResponse = z.infer<typeof UploadProductsResponseSchema>;

export const fetchExcelTemplateForProducts = async (bearerToken) => {
  try {
    const response = await axiosInstance.get(`${apiUrl}/excel/template/201v1`, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      responseType: "arraybuffer", // Ensure the response is treated as binary data
    });
    return response.data; // Return only the raw data
  } catch (error) {
    console.error("Failed to fetch Excel template:", error);
    throw error;
  }
};

export const postExcelProductTemplate = async (
  file: File,
  bearerToken: string,
  companyId: number,
  organizationId?: number,
  existingReportId?: number
): Promise<
  | {
    responseType: "excel";
    data: ArrayBuffer;
    warnings?: string[];
    errors?: string[];
  }
  | { responseType: "json"; data: UploadProductsResponse }
  | void
> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const url = organizationId
      ? `${apiUrl}/Excel/${companyId}/UploadProducts?organizationId=${organizationId}`
      : `${apiUrl}/Excel/${companyId}/UploadProducts`;

    const response = await axiosInstance.post(url, formData, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      responseType: "arraybuffer", // Ensure binary data is handled correctly
    });

    const contentType = response.headers["content-type"];
    if (contentType.includes("application/json")) {
      // Parse JSON response
      const text = new TextDecoder().decode(response.data);
      return {
        responseType: "json",
        data: validateWithSchema(UploadProductsResponseSchema, JSON.parse(text)),
      };
    } else if (
      contentType.includes(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      )
    ) {
      // Return binary data for Excel file, along with any warnings/errors
      // reported via headers since the body can't carry JSON here.
      return {
        responseType: "excel",
        data: response.data,
        warnings: extractHeaderStringArray(response.headers, "x-sumi-warnings"),
        errors: extractHeaderStringArray(response.headers, "x-sumi-errors"),
      };
    }
  } catch (error) {
    console.error("Failed to upload Excel template:", error);

    // Some validation failures come back with a non-2xx status but still
    // carry a full response body; treat those as a normal summary instead
    // of throwing a generic error.
    const errorJson = extractArrayBufferErrorJson(error);
    if (errorJson) {
      return {
        responseType: "json",
        data: validateWithSchema(UploadProductsResponseSchema, errorJson),
      };
    }

    const message = extractArrayBufferErrorMessage(error);
    throw message ? new Error(message) : error;
  }
};
